import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { storableError } from '../util/errors';
import * as log from '../util/log';
import { createImageVariantConfig } from '../util/sdkLoader';
import { addMarketplaceEntities } from './marketplaceData.duck';

/**
 * Homepage Featured Rentals: operator-curated listings (`meta_isfeatured=yes`)
 * with the same filter pills as SearchPage. Fetched after mount so filter
 * changes can re-query without a page load.
 *
 * The Console field key is `isfeatured` (metadata, not publicData), queried as
 * `meta_isfeatured`. The SDK call uses the same query-string-first array form
 * as featuredListings.duck.js so the param is not dropped in serialization.
 */

// Fetch a few extra so View All can tell if the query has more than one row.
const QUERY_PAGE_SIZE = 10;
const FEATURED_FIELD = 'isfeatured';
const FEATURED_QUERY = `meta_${FEATURED_FIELD}=yes`;

const isFeaturedListing = listing => {
  const featured = listing?.attributes?.metadata?.[FEATURED_FIELD];
  return featured === 'yes' || featured === true || featured === 'true';
};

const fetchFeaturedRentalsPayloadCreator = (arg, thunkAPI) => {
  const { extra: sdk, dispatch, rejectWithValue } = thunkAPI;
  const { queryParams = {}, listingImageConfig } = arg;
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
    listingImageConfig || {};
  const aspectRatio = aspectHeight / aspectWidth;

  return sdk.listings
    .query([
      FEATURED_QUERY,
      {
        ...queryParams,
        pub_listingType: 'rent',
        page: 1,
        perPage: QUERY_PAGE_SIZE,
        minStock: 1,
        stockMode: 'match-undefined',
        include: ['images', 'author'],
        'fields.listing': [
          'title',
          'price',
          'deleted',
          'state',
          'publicData.listingType',
          'publicData.transactionProcessAlias',
          'publicData.unitType',
          'publicData.cardStyle',
          'publicData.retail_value',
          'publicData.purchase_price',
          'publicData.designer',
          'publicData.occasion',
          'publicData.priceVariationsEnabled',
          'publicData.priceVariants',
          'metadata',
        ],
        'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
        'fields.image': [
          `variants.${variantPrefix}`,
          `variants.${variantPrefix}-2x`,
          'variants.scaled-small',
          'variants.scaled-medium',
        ],
        ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
        ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
        'limit.images': 1,
      },
    ])
    .then(response => {
      const returned = response.data.data || [];
      const featuredListings = returned.filter(isFeaturedListing);
      dispatch(
        addMarketplaceEntities({
          ...response,
          data: {
            ...response.data,
            data: featuredListings,
          },
        })
      );
      const listingIds = featuredListings.map(l => l.id);
      // When every returned row is featured, the API applied the metadata
      // filter and meta.totalItems is the real curated count. Otherwise we
      // only know how many featured listings were in this page.
      const apiFiltered = featuredListings.length === returned.length;
      const totalItems = apiFiltered
        ? response.data.meta?.totalItems ?? featuredListings.length
        : featuredListings.length;
      return { listingIds, totalItems };
    })
    .catch(e => {
      log.error(e, 'featured-rentals-fetch-failed');
      return rejectWithValue(storableError(e));
    });
};

export const fetchFeaturedRentals = createAsyncThunk(
  'featuredRentals/fetchFeaturedRentals',
  fetchFeaturedRentalsPayloadCreator
);

const initialState = {
  listingIds: [],
  totalItems: 0,
  inProgress: false,
  fetched: false,
  error: null,
};

const featuredRentalsSlice = createSlice({
  name: 'featuredRentals',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchFeaturedRentals.pending, state => {
        state.inProgress = true;
        state.error = null;
      })
      .addCase(fetchFeaturedRentals.fulfilled, (state, action) => {
        state.listingIds = action.payload.listingIds;
        state.totalItems = action.payload.totalItems;
        state.inProgress = false;
        state.fetched = true;
      })
      .addCase(fetchFeaturedRentals.rejected, (state, action) => {
        state.listingIds = [];
        state.totalItems = 0;
        state.inProgress = false;
        state.fetched = true;
        state.error = action.payload;
      });
  },
});

export default featuredRentalsSlice.reducer;
